import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminService from '../../../services/adminService';

const statusesOrder = ['todo', 'in_progress', 'blocked', 'review', 'no_status'];

const PendingTasksPage = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('kanban'); // 'kanban' | 'calendar'
  const [filters, setFilters] = useState({
    assignee: '',
    caseSearch: '',
    status: '',
    dueStart: '',
    dueEnd: ''
  });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      const { data, error: err } = await AdminService.getPendingTasks({
        assignee: filters.assignee || null,
        caseSearch: filters.caseSearch || null,
        status: filters.status || null,
        dueStart: filters.dueStart || null,
        dueEnd: filters.dueEnd || null
      });
      if (err) {
        setError(err);
      } else {
        setTasks(data || []);
      }
      setLoading(false);
    };
    load();
  }, [filters]);

  const groupedByStatus = useMemo(() => {
    const groups = {};
    tasks.forEach((task) => {
      const key = task.status || 'no_status';
      if (!groups[key]) groups[key] = [];
      groups[key].push(task);
    });
    return groups;
  }, [tasks]);

  const calendarBuckets = useMemo(() => {
    const buckets = {};
    tasks.forEach((task) => {
      if (!task.due_date) {
        if (!buckets.noDate) buckets.noDate = [];
        buckets.noDate.push(task);
        return;
      }
      const key = new Date(task.due_date).toISOString().split('T')[0];
      if (!buckets[key]) buckets[key] = [];
      buckets[key].push(task);
    });
    return buckets;
  }, [tasks]);

  const handleInputChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const renderTaskCard = (task) => (
    <div
      key={task.id}
      className="bg-gray-800 border border-gray-700 rounded p-3 mb-3 shadow-sm cursor-pointer hover:border-blue-500 transition"
      onClick={() => navigate(`/admin/tasks/${task.id}`)}
    >
      <div className="flex justify-between items-start gap-2">
        <div>
          <div className="text-sm font-semibold text-white">{task.title}</div>
          <div className="text-xs text-gray-400 mt-1">
            {task?.cases?.title ? `Case: ${task.cases.title}` : 'No case'}
          </div>
        </div>
        <div className="text-right text-xs text-gray-400">
          {task.due_date ? new Date(task.due_date).toLocaleDateString() : 'No due date'}
        </div>
      </div>
      <div className="mt-2 flex justify-between items-center text-xs text-gray-400">
        <span>{task.assigned_to || 'Unassigned'}</span>
        <span className="uppercase">{task.status || 'No status'}</span>
      </div>
    </div>
  );

  return (
    <div className="p-6 bg-gray-900 text-white min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Pending Tasks</h1>
          <p className="text-sm text-gray-400">All tasks not completed, with Kanban and Calendar views.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            className={`px-3 py-1 rounded text-sm border ${viewMode === 'kanban' ? 'bg-blue-600 border-blue-500' : 'border-gray-700 bg-gray-800'}`}
            onClick={() => setViewMode('kanban')}
          >
            Kanban
          </button>
          <button
            className={`px-3 py-1 rounded text-sm border ${viewMode === 'calendar' ? 'bg-blue-600 border-blue-500' : 'border-gray-700 bg-gray-800'}`}
            onClick={() => setViewMode('calendar')}
          >
            Calendar
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-6">
        <input
          className="bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm w-full"
          placeholder="Assignee"
          value={filters.assignee}
          onChange={(e) => handleInputChange('assignee', e.target.value)}
        />
        <input
          className="bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm w-full"
          placeholder="Case title"
          value={filters.caseSearch}
          onChange={(e) => handleInputChange('caseSearch', e.target.value)}
        />
        <select
          className="bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm w-full"
          value={filters.status}
          onChange={(e) => handleInputChange('status', e.target.value)}
        >
          <option value="">Status (any)</option>
          <option value="todo">To Do</option>
          <option value="in_progress">In Progress</option>
          <option value="blocked">Blocked</option>
          <option value="review">Review</option>
        </select>
        <input
          type="date"
          className="bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm w-full"
          value={filters.dueStart}
          onChange={(e) => handleInputChange('dueStart', e.target.value)}
        />
        <input
          type="date"
          className="bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm w-full"
          value={filters.dueEnd}
          onChange={(e) => handleInputChange('dueEnd', e.target.value)}
        />
      </div>

      {loading && <div className="text-sm text-gray-400">Loading pending tasks…</div>}
      {error && <div className="text-sm text-red-400">Error: {error}</div>}

      {!loading && !error && (
        <>
          {viewMode === 'kanban' && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {statusesOrder.map((statusKey) => (
                <div key={statusKey} className="bg-gray-800 border border-gray-700 rounded p-3">
                  <div className="flex justify-between items-center mb-2">
                    <div className="text-sm font-semibold uppercase text-gray-300">
                      {statusKey === 'no_status' ? 'No Status' : statusKey.replace('_', ' ')}
                    </div>
                    <div className="text-xs text-gray-400">
                      {(groupedByStatus[statusKey] || []).length}
                    </div>
                  </div>
                  <div>
                    {(groupedByStatus[statusKey] || []).map(renderTaskCard)}
                    {(groupedByStatus[statusKey] || []).length === 0 && (
                      <div className="text-xs text-gray-500">No tasks</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {viewMode === 'calendar' && (
            <div className="space-y-3">
              {Object.keys(calendarBuckets).length === 0 && (
                <div className="text-sm text-gray-500">No tasks available.</div>
              )}
              {Object.entries(calendarBuckets)
                .sort(([a], [b]) => (a === 'noDate' ? 1 : b === 'noDate' ? -1 : a.localeCompare(b)))
                .map(([dateKey, bucket]) => (
                  <div key={dateKey} className="bg-gray-800 border border-gray-700 rounded p-3">
                    <div className="text-sm font-semibold text-gray-200 mb-2">
                      {dateKey === 'noDate' ? 'No due date' : new Date(dateKey).toLocaleDateString()}
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {bucket.map((task) => (
                        <div
                          key={task.id}
                          className="bg-gray-900 border border-gray-700 rounded p-3 text-sm cursor-pointer hover:border-blue-500 transition"
                          onClick={() => navigate(`/admin/tasks/${task.id}`)}
                        >
                          <div className="font-semibold text-white">{task.title}</div>
                          <div className="text-xs text-gray-400 mt-1">
                            {task?.cases?.title ? `Case: ${task.cases.title}` : 'No case'}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">{task.assigned_to || 'Unassigned'}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PendingTasksPage;
